import { useState, useEffect, useRef } from 'react';
import { useVoiceSession } from '../../hooks/useVoiceSession';

const TOPIC_LABELS = {
  lifestyle: 'Lifestyle',
  background: 'Background',
  physical: 'Physical',
  personality: 'Personality',
};

const ALL_TOPICS = ['lifestyle', 'background', 'physical', 'personality'];

export default function VoiceOnboarding({ onComplete, onPause, userName, resumeSessionId }) {
  const {
    turnState,
    transcript,
    currentUserText,
    topicsCovered,
    readyToWrap,
    extractedCount,
    error,
    profile,
    isConnected,
    generatingProfile,
    isPaused,
    startSession,
    resumeSession,
    pauseSession,
    toggleRecording,
    endSession,
    TURN_STATES,
  } = useVoiceSession();

  const [hasStarted, setHasStarted] = useState(false);
  const [micPermission, setMicPermission] = useState(null); // null | 'granted' | 'denied'
  const transcriptEndRef = useRef(null);
  const isResumeMode = !!resumeSessionId;

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript, currentUserText]);

  // When profile is generated, pass it up
  useEffect(() => {
    if (profile) {
      onComplete(profile);
    }
  }, [profile, onComplete]);

  // When session is paused, notify parent
  useEffect(() => {
    if (isPaused && onPause) {
      onPause();
    }
  }, [isPaused, onPause]);

  // Check mic permission
  const checkMicPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setMicPermission('granted');
      return true;
    } catch {
      setMicPermission('denied');
      return false;
    }
  };

  const handleStart = async () => {
    const granted = await checkMicPermission();
    if (granted) {
      setHasStarted(true);
      if (isResumeMode) {
        resumeSession(resumeSessionId);
      } else {
        startSession();
      }
    }
  };

  // Pre-start screen
  if (!hasStarted) {
    return (
      <div style={styles.container}>
        <div style={styles.introCard}>
          <div style={styles.micIcon}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#E8576D" strokeWidth="2">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          </div>
          <h2 style={styles.introTitle}>
            {isResumeMode ? 'Welcome back!' : "Let's get to know you!"}
          </h2>
          <p style={styles.introText}>
            {isResumeMode
              ? "Ready to pick up where you left off? Nova remembers your conversation and will continue building your profile."
              : "Nova, our AI assistant, will have a quick chat with you to learn about your interests, lifestyle, and what you're looking for. It's like talking to a friend — just be yourself!"}
          </p>
          {!isResumeMode && (
            <p style={styles.introSubtext}>
              The conversation takes about 5-8 minutes. We'll use what you share to build your
              dating profile automatically.
            </p>
          )}
          {micPermission === 'denied' && (
            <p style={styles.errorText}>
              Microphone access is required. Please enable it in your browser settings.
            </p>
          )}
          <button style={styles.startButton} onClick={handleStart}>
            {isResumeMode ? 'Resume Conversation' : 'Start Conversation'}
          </button>
        </div>
      </div>
    );
  }

  // Generating profile screen
  if (generatingProfile) {
    return (
      <div style={styles.container}>
        <div style={styles.generatingCard}>
          <div style={styles.spinner} />
          <h3 style={styles.generatingTitle}>Creating your profile...</h3>
          <p style={styles.generatingText}>
            Nova is analyzing your conversation to build a personalized dating profile.
            This usually takes about 10-15 seconds.
          </p>
        </div>
      </div>
    );
  }

  const isUserSpeaking = turnState === TURN_STATES.USER_SPEAKING;
  const isProcessing = turnState === TURN_STATES.PROCESSING;
  const isAISpeaking = turnState === TURN_STATES.AI_SPEAKING;
  const canSpeak = turnState === TURN_STATES.IDLE;

  return (
    <div style={styles.container}>
      {/* Topic progress */}
      <div style={styles.topicBar}>
        {ALL_TOPICS.map((topic) => (
          <div
            key={topic}
            style={{
              ...styles.topicChip,
              ...(topicsCovered.includes(topic) ? styles.topicChipActive : {}),
            }}
          >
            {topicsCovered.includes(topic) && (
              <span style={styles.checkMark}>&#10003; </span>
            )}
            {TOPIC_LABELS[topic]}
          </div>
        ))}
      </div>

      {/* Extracted count */}
      {extractedCount > 0 && (
        <div style={styles.extractedBadge}>
          {extractedCount} data points collected
        </div>
      )}

      {/* Transcript area */}
      <div style={styles.transcriptArea}>
        {transcript.map((msg, i) => (
          <div
            key={i}
            style={{
              ...styles.messageBubble,
              ...(msg.role === 'user' ? styles.userBubble : styles.aiBubble),
            }}
          >
            <span style={styles.messageLabel}>
              {msg.role === 'user' ? 'You' : 'Nova'}
            </span>
            <p style={styles.messageText}>{msg.content}</p>
          </div>
        ))}

        {/* Current user speech (interim) */}
        {currentUserText && (
          <div style={{ ...styles.messageBubble, ...styles.userBubble, opacity: 0.7 }}>
            <span style={styles.messageLabel}>You</span>
            <p style={styles.messageText}>{currentUserText}</p>
          </div>
        )}

        {/* Processing indicator */}
        {isProcessing && (
          <div style={{ ...styles.messageBubble, ...styles.aiBubble }}>
            <span style={styles.messageLabel}>Nova</span>
            <p style={styles.messageText}>
              <span style={styles.dots}>
                <span style={styles.dot}>.</span>
                <span style={{ ...styles.dot, animationDelay: '0.2s' }}>.</span>
                <span style={{ ...styles.dot, animationDelay: '0.4s' }}>.</span>
              </span>
            </p>
          </div>
        )}

        <div ref={transcriptEndRef} />
      </div>

      {/* Error display */}
      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* Controls */}
      <div style={styles.controls}>
        {/* Mic button */}
        <button
          style={{
            ...styles.micButton,
            ...(isUserSpeaking ? styles.micButtonActive : {}),
            ...((!canSpeak && !isUserSpeaking) ? styles.micButtonDisabled : {}),
          }}
          onClick={toggleRecording}
          disabled={!canSpeak && !isUserSpeaking}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
            stroke={isUserSpeaking ? '#fff' : '#E8576D'} strokeWidth="2">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        </button>

        <p style={styles.statusText}>
          {isUserSpeaking && 'Listening... tap to finish'}
          {isProcessing && 'Nova is thinking...'}
          {isAISpeaking && 'Nova is speaking...'}
          {canSpeak && 'Tap the mic to speak'}
        </p>

        {/* End conversation button */}
        {readyToWrap && (
          <button style={styles.endButton} onClick={endSession}>
            End Conversation
          </button>
        )}

        {/* Early end (always available after some turns) */}
        {!readyToWrap && transcript.length >= 6 && (
          <button
            style={{ ...styles.endButton, ...styles.endButtonEarly }}
            onClick={endSession}
          >
            End Early
          </button>
        )}

        {/* Continue later (visible after at least one exchange) */}
        {onPause && transcript.length >= 2 && (
          <button
            style={styles.continueLaterButton}
            onClick={pauseSession}
          >
            Continue later
          </button>
        )}
      </div>
    </div>
  );
}

const pulseKeyframes = `
@keyframes pulse {
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(232, 87, 109, 0.4); }
  50% { transform: scale(1.05); box-shadow: 0 0 0 15px rgba(232, 87, 109, 0); }
}
@keyframes dotBounce {
  0%, 80%, 100% { opacity: 0.3; }
  40% { opacity: 1; }
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
`;

// Inject keyframes
if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.textContent = pulseKeyframes;
  document.head.appendChild(styleEl);
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: '500px',
    maxHeight: '700px',
    backgroundColor: '#FAFAFA',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  introCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 24px',
    textAlign: 'center',
    flex: 1,
  },
  micIcon: {
    marginBottom: '24px',
    opacity: 0.9,
  },
  introTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '12px',
    fontFamily: 'DM Sans, sans-serif',
  },
  introText: {
    fontSize: '16px',
    color: '#6B7280',
    lineHeight: '1.6',
    maxWidth: '400px',
    marginBottom: '12px',
  },
  introSubtext: {
    fontSize: '14px',
    color: '#9CA3AF',
    lineHeight: '1.5',
    maxWidth: '400px',
    marginBottom: '24px',
  },
  startButton: {
    background: '#E8576D',
    color: '#fff',
    border: 'none',
    borderRadius: '24px',
    padding: '14px 36px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif',
  },
  errorText: {
    color: '#ef4444',
    fontSize: '14px',
    marginBottom: '16px',
  },
  topicBar: {
    display: 'flex',
    gap: '8px',
    padding: '12px 16px',
    flexWrap: 'wrap',
    borderBottom: '1px solid #E5E7EB',
  },
  topicChip: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#9CA3AF',
    backgroundColor: '#F3F4F6',
    fontFamily: 'DM Sans, sans-serif',
  },
  topicChipActive: {
    backgroundColor: 'rgba(232, 87, 109, 0.15)',
    color: '#E8576D',
  },
  checkMark: {
    fontSize: '10px',
  },
  extractedBadge: {
    textAlign: 'center',
    padding: '6px',
    fontSize: '12px',
    color: '#9CA3AF',
    borderBottom: '1px solid #E5E7EB',
  },
  transcriptArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  messageBubble: {
    padding: '10px 14px',
    borderRadius: '16px',
    maxWidth: '85%',
  },
  userBubble: {
    alignSelf: 'flex-end',
    backgroundColor: '#E8576D',
    color: '#fff',
  },
  aiBubble: {
    alignSelf: 'flex-start',
    backgroundColor: '#F3F4F6',
    color: '#111827',
  },
  messageLabel: {
    fontSize: '11px',
    fontWeight: '600',
    opacity: 0.7,
    display: 'block',
    marginBottom: '2px',
    fontFamily: 'DM Sans, sans-serif',
  },
  messageText: {
    margin: 0,
    fontSize: '14px',
    lineHeight: '1.5',
    fontFamily: 'DM Sans, sans-serif',
  },
  dots: {
    display: 'inline-flex',
    gap: '2px',
  },
  dot: {
    animation: 'dotBounce 1.4s infinite ease-in-out',
    fontSize: '24px',
    lineHeight: '10px',
  },
  errorBanner: {
    padding: '8px 16px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#ef4444',
    fontSize: '13px',
    textAlign: 'center',
  },
  controls: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px',
    gap: '12px',
    borderTop: '1px solid #E5E7EB',
  },
  micButton: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    border: '2px solid #E8576D',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  micButtonActive: {
    backgroundColor: '#E8576D',
    animation: 'pulse 1.5s infinite',
  },
  micButtonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    border: '2px solid #D1D5DB',
  },
  statusText: {
    fontSize: '13px',
    color: '#9CA3AF',
    fontFamily: 'DM Sans, sans-serif',
    margin: 0,
  },
  endButton: {
    background: 'transparent',
    border: '1px solid #E8576D',
    color: '#E8576D',
    borderRadius: '20px',
    padding: '8px 24px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif',
  },
  endButtonEarly: {
    borderColor: '#D1D5DB',
    color: '#9CA3AF',
  },
  continueLaterButton: {
    background: 'transparent',
    border: 'none',
    color: '#9CA3AF',
    fontSize: '13px',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontFamily: 'DM Sans, sans-serif',
    padding: '4px 8px',
  },
  generatingCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 24px',
    textAlign: 'center',
    flex: 1,
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '3px solid #E5E7EB',
    borderTopColor: '#E8576D',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '24px',
  },
  generatingTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '8px',
    fontFamily: 'DM Sans, sans-serif',
  },
  generatingText: {
    fontSize: '14px',
    color: '#9CA3AF',
    maxWidth: '300px',
    lineHeight: '1.5',
  },
};
